/**
 * Visual Editor — Chart styling editor sidebar.
 *
 * Orchestrates sections:
 * 1. Chart Type        — compact icon grid
 * 2. Fields & Measures — field mapping editor
 * 3. Chart Style       — type-specific controls (extracted components)
 * 4. Colors            — palette, color mode, per-series overrides
 * 5. Data Labels       — visibility, position, formatting
 * 6. Axis & Scale      — titles, show/hide, grid lines, zero-start, reverse, scale
 * 7. Legend & Tooltip   — legend position, tooltip options
 */
import React, { useCallback, useMemo, useRef } from "react";
import {
  Checkbox,
  Field,
  Input,
  Label,
  Radio,
  RadioGroup,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Chart, DatasetViewColumn } from "../../api/types";
import FieldMappingEditor from "./fieldMapping";
import { migrateFieldsForChartType, classifyModel } from "./fieldMapping/migrateFields";
import PalettePicker from "./PalettePicker";
import VisualSection from "./VisualSection";
import {
  LineAreaStyle,
  BarStyle,
  PieStyle,
  ScatterStyle,
  HeatmapStyle,
  FunnelStyle,
  KpiStyle,
} from "./chartStyleSections";
import {
  type ChartEditorState,
  type ChartStyleState,
  type ChartFieldConfig,
  type TypeStyle,
  type ChartTypeOption,
  type LabelPosition,
  type LegendPosition,
  type ScaleType,
  BREAKDOWN_RULES,
  getChartStyleDefaults,
  CHART_TYPE_OPTIONS,
  getPaletteColors,
  getCapabilities,
  getDefaultLabelPosition,
  getLabelPositionConfig,
} from "./visualConfig";

// ── Props ──────────────────────────────────────────────────────────────────

interface VisualEditorProps {
  chart: Chart;
  previewChart?: Chart | null;
  editorState: ChartEditorState;
  initialEditorState: ChartEditorState;
  onEditorStateChange: (state: ChartEditorState) => void;
  seriesNames?: string[];
  /** X-axis category names for per-category color overrides */
  categoryNames?: string[];
  viewColumns?: DatasetViewColumn[];
  /** Dashboard-level theme. Shown as "(Default)" in palette picker. */
  dashboardTheme?: string;
}

// ── Styles ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  panel: {
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarGutter: "stable",
    padding: "0 16px 16px 16px",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  chartTypeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "8px",
  },
  chartTypeCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    height: "56px",
    padding: "8px 6px",
    borderRadius: "6px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: "pointer",
    backgroundColor: tokens.colorNeutralBackground1,
    ":hover": {
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  chartTypeIcon: {
    fontSize: "14px",
    color: tokens.colorNeutralForeground1,
  },
  chartTypeLabel: {
    fontSize: "12px",
    lineHeight: "1",
  },
  colorRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  colorInput: {
    width: "32px",
    height: "32px",
    border: "none",
    padding: "0",
    cursor: "pointer",
    borderRadius: "6px",
    backgroundColor: "transparent",
  },
  colorSeriesName: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  axisRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  axisInput: {
    flex: 1,
    minWidth: 0,
  },
});

// ── Component ──────────────────────────────────────────────────────────────

const VisualEditor: React.FC<VisualEditorProps> = ({
  chart: _chart,
  previewChart: _previewChart,
  editorState,
  initialEditorState,
  onEditorStateChange,
  seriesNames = [],
  categoryNames = [],
  viewColumns = [],
  dashboardTheme,
}) => {
  const styles = useStyles();
  const ct = editorState.chartType;
  const s = editorState.chartStyle;
  const ts = s.typeStyle;
  // KPI without a datetime column has no sparkline or previous-period comparison,
  // so the Chart Style (sparkline settings) and Colors (palette/series color) sections
  // are irrelevant — hide them to avoid confusing the user.
  const caps = useMemo(() => {
    const base = getCapabilities(ct);
    if (ct === 'kpi') {
      const hasDatetime = viewColumns?.some(c => c.semantic_type === 'time');
      if (!hasDatetime) return { ...base, chartStyle: false, colors: false };
    }
    return base;
  }, [ct, viewColumns]);

  // ── Update helpers ─────────────────────────────────────────────────────

  /** Patch chartStyle fields. */
  const updateStyle = useCallback(
    (patch: Partial<ChartStyleState>) => {
      onEditorStateChange({
        ...editorState,
        chartStyle: { ...editorState.chartStyle, ...patch },
      });
    },
    [editorState, onEditorStateChange],
  );

  /** Patch typeStyle (chart-type-specific: stacking, markers, etc.). */
  const updateTypeStyle = useCallback(
    (patch: Partial<TypeStyle>) => {
      onEditorStateChange({
        ...editorState,
        chartStyle: {
          ...editorState.chartStyle,
          typeStyle: { ...editorState.chartStyle.typeStyle, ...patch },
        },
      });
    },
    [editorState, onEditorStateChange],
  );

  // Snapshot cache: preserves field config per chart type so round-trip switches
  // (e.g. bar→pie→bar or bar→kpi→bar) restore the original fields losslessly.
  const fieldSnapshotsRef = useRef<Partial<Record<ChartTypeOption, ChartFieldConfig>>>({});

  const handleChartTypeChange = useCallback(
    (newType: ChartTypeOption) => {
      // Save current fields under the exact source chart type before switching
      if (editorState.fields && Object.keys(editorState.fields).length > 0) {
        fieldSnapshotsRef.current[editorState.chartType] = { ...editorState.fields };
      }

      // Restore from snapshot if the target type was visited before, otherwise migrate.
      let resolvedFields: ChartFieldConfig;
      const snapshot = fieldSnapshotsRef.current[newType];
      if (snapshot) {
        resolvedFields = { ...snapshot };
      } else {
        const snaps = fieldSnapshotsRef.current;
        let bestSource = editorState.fields ?? {};
        let bestSourceType = editorState.chartType;
        const modelPriority: Array<'xy' | 'table' | 'kpi'> = ['xy', 'table', 'kpi'];
        for (const model of modelPriority) {
          const found = Object.entries(snaps).find(
            ([type, cfg]) => cfg && classifyModel(type as ChartTypeOption) === model && Object.keys(cfg).length > 0,
          );
          if (found) {
            bestSource = found[1]!;
            bestSourceType = found[0] as ChartTypeOption;
            break;
          }
        }
        resolvedFields = migrateFieldsForChartType(
          bestSource,
          bestSourceType,
          newType,
          viewColumns ?? [],
        );
      }

      const next: ChartEditorState = {
        ...editorState,
        chartType: newType,
        chartStyle: {
          // Apply type defaults first, then preserve user customizations
          // (palette, legend, seriesColors, etc.), then reset only
          // type-specific overrides (typeStyle, labelPosition).
          ...getChartStyleDefaults(newType),
          ...editorState.chartStyle,
          typeStyle: getChartStyleDefaults(newType).typeStyle,
          labelPosition: getDefaultLabelPosition(newType),
        },
        fields: Object.keys(resolvedFields).length > 0 ? resolvedFields : undefined,
      };

      // Strip breakdown/series if the new type doesn't support it
      if (!BREAKDOWN_RULES[newType].enabled && next.fields?.series) {
        const { series: _, ...restFields } = next.fields;
        next.fields = Object.keys(restFields).length > 0 ? restFields : undefined;
      }
      onEditorStateChange(next);
    },
    [editorState, onEditorStateChange, viewColumns],
  );

  // ── Section reset handlers ─────────────────────────────────────────────
  // Reset = revert to the config that was loaded from DB (initialEditorState).

  const resetChartStyle = useCallback(() => {
    updateTypeStyle({ ...initialEditorState.chartStyle.typeStyle });
  }, [initialEditorState.chartStyle.typeStyle, updateTypeStyle]);

  const resetColors = useCallback(() => {
    updateStyle({
      palette: initialEditorState.chartStyle.palette,
      seriesColors: { ...initialEditorState.chartStyle.seriesColors },
      typeStyle: { ...editorState.chartStyle.typeStyle, colorMode: initialEditorState.chartStyle.typeStyle.colorMode },
    });
  }, [initialEditorState, editorState.chartStyle.typeStyle, updateStyle]);

  const resetLabels = useCallback(() => {
    updateStyle({
      showLabels: initialEditorState.chartStyle.showLabels,
      showPercent: initialEditorState.chartStyle.showPercent,
      abbreviateNumbers: initialEditorState.chartStyle.abbreviateNumbers,
      showTotal: initialEditorState.chartStyle.showTotal,
      labelPosition: initialEditorState.chartStyle.labelPosition,
    });
  }, [initialEditorState, updateStyle]);

  const resetAxis = useCallback(() => {
    updateStyle({
      showXAxisLabel: initialEditorState.chartStyle.showXAxisLabel,
      showYAxisLabel: initialEditorState.chartStyle.showYAxisLabel,
      xAxisTitle: initialEditorState.chartStyle.xAxisTitle,
      yAxisTitle: initialEditorState.chartStyle.yAxisTitle,
      startAtZero: initialEditorState.chartStyle.startAtZero,
      reverseAxis: initialEditorState.chartStyle.reverseAxis,
      scaleType: initialEditorState.chartStyle.scaleType,
      showGridLines: initialEditorState.chartStyle.showGridLines,
    });
  }, [initialEditorState, updateStyle]);

  const resetLegend = useCallback(() => {
    updateStyle({
      showLegend: initialEditorState.chartStyle.showLegend,
      legendPosition: initialEditorState.chartStyle.legendPosition,
      sharedTooltip: initialEditorState.chartStyle.sharedTooltip,
      crosshair: initialEditorState.chartStyle.crosshair,
    });
  }, [initialEditorState, updateStyle]);

  // ── Dirty detection ────────────────────────────────────────────────────

  const isChartStyleDirty = useMemo(
    () => JSON.stringify(s.typeStyle) !== JSON.stringify(initialEditorState.chartStyle.typeStyle),
    [s.typeStyle, initialEditorState.chartStyle.typeStyle],
  );

  const isColorsDirty = useMemo(
    () =>
      s.palette !== initialEditorState.chartStyle.palette ||
      JSON.stringify(s.seriesColors) !== JSON.stringify(initialEditorState.chartStyle.seriesColors) ||
      s.typeStyle.colorMode !== initialEditorState.chartStyle.typeStyle.colorMode,
    [s.palette, s.seriesColors, s.typeStyle.colorMode, initialEditorState.chartStyle.palette, initialEditorState.chartStyle.seriesColors, initialEditorState.chartStyle.typeStyle.colorMode],
  );

  const isLabelsDirty =
    s.showLabels !== initialEditorState.chartStyle.showLabels ||
    s.showPercent !== initialEditorState.chartStyle.showPercent ||
    s.abbreviateNumbers !== initialEditorState.chartStyle.abbreviateNumbers ||
    s.showTotal !== initialEditorState.chartStyle.showTotal ||
    s.labelPosition !== initialEditorState.chartStyle.labelPosition;

  const isAxisDirty =
    s.showXAxisLabel !== initialEditorState.chartStyle.showXAxisLabel ||
    s.showYAxisLabel !== initialEditorState.chartStyle.showYAxisLabel ||
    s.xAxisTitle !== initialEditorState.chartStyle.xAxisTitle ||
    s.yAxisTitle !== initialEditorState.chartStyle.yAxisTitle ||
    s.startAtZero !== initialEditorState.chartStyle.startAtZero ||
    s.reverseAxis !== initialEditorState.chartStyle.reverseAxis ||
    s.scaleType !== initialEditorState.chartStyle.scaleType ||
    s.showGridLines !== initialEditorState.chartStyle.showGridLines;

  const isLegendDirty =
    s.showLegend !== initialEditorState.chartStyle.showLegend ||
    s.legendPosition !== initialEditorState.chartStyle.legendPosition ||
    s.sharedTooltip !== initialEditorState.chartStyle.sharedTooltip ||
    s.crosshair !== initialEditorState.chartStyle.crosshair;

  // ── Chart style dispatcher ─────────────────────────────────────────────

  const renderChartStyleContent = () => {
    const props = { chartStyle: ts, onStyleChange: updateTypeStyle };
    switch (ct) {
      case "kpi":
        return <KpiStyle {...props} />;
      case "table":
        return null;
      case "line":
        return <LineAreaStyle {...props} isArea={false} />;
      case "area":
        return <LineAreaStyle {...props} isArea={true} />;
      case "bar":
      case "histogram":
        return <BarStyle {...props} chartType={ct} />;
      case "pie":
        return <PieStyle {...props} />;
      case "scatter":
        return <ScatterStyle {...props} />;
      case "heatmap":
        return <HeatmapStyle {...props} palette={s.palette} />;
      case "funnel":
        return <FunnelStyle {...props} />;
      default:
        return null;
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className={styles.panel}>
      {/* ─── Chart Type ─────────────────────────────────────────────── */}
        <div className={styles.chartTypeGrid} style={{ paddingTop: '12px', paddingBottom: '12px' }}>
          {CHART_TYPE_OPTIONS.map((opt) => (
            <div
              key={opt.value}
              className={styles.chartTypeCard}
              style={
                ct === opt.value
                  ? { borderColor: tokens.colorBrandStroke1, backgroundColor: tokens.colorBrandBackground2 }
                  : undefined
              }
              onClick={() => handleChartTypeChange(opt.value)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleChartTypeChange(opt.value);
              }}
            >
              <opt.icon className={styles.chartTypeIcon} />
              <span className={styles.chartTypeLabel}>{opt.label}</span>
            </div>
          ))}
        </div>

      {/* ─── Fields & Measures ──────────────────────────────────────── */}
      {viewColumns.length > 0 && (
        <VisualSection title="Fields & measures">
          <FieldMappingEditor
            editorState={editorState}
            onEditorStateChange={onEditorStateChange}
            viewColumns={viewColumns}
            chartType={ct}
          />
        </VisualSection>
      )}

      {/* ─── Chart Style ────────────────────────────────────────────── */}
      {caps.chartStyle && (
      <VisualSection title="Chart style" onReset={resetChartStyle} isDirty={isChartStyleDirty}>
        {renderChartStyleContent()}
      </VisualSection>
      )}

      {/* ─── Colors ─────────────────────────────────────────────────── */}
      {caps.colors && (
      <VisualSection title="Colors" onReset={resetColors} isDirty={isColorsDirty}>
        {!caps.palette ? (
          <Label size="small" style={{ color: tokens.colorNeutralForeground3 }}>
            Colors are controlled by the gradient setting in Chart Style.
          </Label>
        ) : (
        <>
        <Label size="small" weight="semibold">Palette</Label>
        <PalettePicker
          value={s.palette}
          onChange={(palette) => updateStyle({ palette })}
          defaultIndicator={dashboardTheme as import("./visualConfig/types").PaletteOption | undefined}
        />
        {caps.colorMode && (
          <Field label="Color mode">
            <RadioGroup
              layout="horizontal"
              value={ts.colorMode ?? 'series'}
              disabled={seriesNames.length > 1}
              onChange={(_, data) => updateTypeStyle({ colorMode: (data.value as 'series' | 'category') ?? 'series' })}
            >
              <Radio value="series" label="Uniform" />
              <Radio value="category" label="By category" />
            </RadioGroup>
          </Field>
        )}
        {caps.seriesColors && (() => {
          const isCategoryMode = ts.colorMode === 'category' && categoryNames.length > 0;
          const colorNames = isCategoryMode ? categoryNames : seriesNames;
          if (colorNames.length === 0) return null;
          const paletteDefaults = getPaletteColors(s.palette, false);
          return (
            <>
              <Label size="small" weight="semibold" style={{ marginTop: "8px" }}>
                {isCategoryMode ? "Category Colors" : "Series Colors"}
              </Label>
              <datalist id="palette-colors">
                {[...new Set(paletteDefaults)].map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              {colorNames.map((name, idx) => {
                const defaultColor = paletteDefaults[idx % paletteDefaults.length];
                return (
                  <div key={name} className={styles.colorRow}>
                    <input
                      type="color"
                      list="palette-colors"
                      className={styles.colorInput}
                      value={(s.seriesColors ?? {})[name] || defaultColor}
                      onChange={(e) =>
                        updateStyle({ seriesColors: { ...(s.seriesColors ?? {}), [name]: e.target.value } })
                      }
                    />
                    <span className={styles.colorSeriesName}>{name}</span>
                  </div>
                );
              })}
            </>
          );
        })()}
        </>
        )}
      </VisualSection>
      )}

      {/* ─── Data Labels ────────────────────────────────────────────── */}
      {caps.dataLabels && (
      <VisualSection title="Data labels" onReset={resetLabels} isDirty={isLabelsDirty}>
        {caps.percent ? (
          <Field label="Display">
            <RadioGroup
              layout="horizontal"
              value={s.showPercent ? 'percent' : s.showLabels ? 'value' : 'none'}
              onChange={(_, data) => {
                switch (data.value) {
                  case 'none':    updateStyle({ showLabels: false, showPercent: false }); break;
                  case 'value':   updateStyle({ showLabels: true,  showPercent: false }); break;
                  case 'percent': updateStyle({ showLabels: false, showPercent: true  }); break;
                }
              }}
            >
              <Radio value="none" label="None" />
              <Radio value="value" label="Value" />
              <Radio value="percent" label="Percent" />
            </RadioGroup>
          </Field>
        ) : (
          <>
            <Checkbox label="Show values" checked={s.showLabels} onChange={(_, data) => updateStyle({ showLabels: !!data.checked })} />
            {caps.stacking && ts.stacking !== 'off' && (
              <Checkbox label="Show total (stacked)" checked={s.showTotal} onChange={(_, data) => updateStyle({ showTotal: !!data.checked })} />
            )}
          </>
        )}
        <Checkbox label="Abbreviate numbers" checked={s.abbreviateNumbers} onChange={(_, data) => updateStyle({ abbreviateNumbers: !!data.checked })} />
        {(s.showLabels || !caps.axis) && getLabelPositionConfig(ct) && (
          <Field label="Label Position">
            <RadioGroup
              layout="horizontal"
              value={s.labelPosition}
              onChange={(_, data) => updateStyle({ labelPosition: (data.value as LabelPosition) ?? getDefaultLabelPosition(ct) })}
            >
              {getLabelPositionConfig(ct)!.positions.map((opt) => (
                <Radio key={opt.value} value={opt.value} label={opt.label} />
              ))}
            </RadioGroup>
          </Field>
        )}
      </VisualSection>
      )}

      {/* ─── Axis & Scale ───────────────────────────────────────────── */}
      {caps.axis && (
        <VisualSection title="Axis & scale" onReset={resetAxis} isDirty={isAxisDirty}>
          <Label size="small" weight="semibold">X axis</Label>
          <div className={styles.axisRow}>
            <Checkbox label="Show" checked={s.showXAxisLabel} onChange={(_, data) => updateStyle({ showXAxisLabel: !!data.checked })} />
            <Input
              className={styles.axisInput}
              value={s.xAxisTitle}
              placeholder={(() => {
                const fm = (_previewChart?.fieldMappings ?? _chart.fieldMappings) as Record<string, any> | undefined;
                if (fm?.x?.label) return fm.x.label;
                const fc = editorState.fields;
                return fc?.x?.column ?? "Category";
              })()}
              disabled={!s.showXAxisLabel}
              onChange={(_, data) => updateStyle({ xAxisTitle: data.value })}
            />
          </div>
          <Label size="small" weight="semibold" style={{ marginTop: 8 }}>Y axis</Label>
          <div className={styles.axisRow}>
            <Checkbox label="Show" checked={s.showYAxisLabel} onChange={(_, data) => updateStyle({ showYAxisLabel: !!data.checked })} />
            <Input
              className={styles.axisInput}
              value={s.yAxisTitle}
              placeholder={(() => {
                const fm = (_previewChart?.fieldMappings ?? _chart.fieldMappings) as Record<string, any> | undefined;
                if (fm?.yDimension?.label) return fm.yDimension.label;
                if (fm?.y && Array.isArray(fm.y) && fm.y.length > 0) {
                  const labels = fm.y.map((f: any) => f.label).filter(Boolean);
                  if (labels.length) return labels.join(' / ');
                }
                const fc = editorState.fields;
                if (fc?.y && fc.y.length > 0) return fc.y[0].column;
                if (seriesNames.length === 1) return seriesNames[0];
                return "Value";
              })()}
              disabled={!s.showYAxisLabel}
              onChange={(_, data) => updateStyle({ yAxisTitle: data.value })}
            />
          </div>
          {caps.scaleControl && (
            <>
              <Checkbox label="Start at zero" checked={s.startAtZero} onChange={(_, data) => updateStyle({ startAtZero: !!data.checked })} style={{ marginTop: 8 }} />
              <Checkbox label="Reverse axis" checked={s.reverseAxis} onChange={(_, data) => updateStyle({ reverseAxis: !!data.checked })} />
              {caps.gridLines && (
                <Checkbox label="Show grid lines" checked={s.showGridLines} onChange={(_, data) => updateStyle({ showGridLines: !!data.checked })} />
              )}
              <Field label="Scale">
                <RadioGroup layout="horizontal" value={s.scaleType} onChange={(_, data) => updateStyle({ scaleType: (data.value as ScaleType) ?? "linear" })}>
                  <Radio value="linear" label="Linear" />
                  <Radio value="log" label="Logarithmic" />
                </RadioGroup>
              </Field>
            </>
          )}
        </VisualSection>
      )}

      {/* ─── Legend & Tooltip ───────────────────────────────────────── */}
      {(caps.legend || caps.sharedTooltip || caps.crosshair) && (
      <VisualSection title={caps.legend ? 'Legend & tooltip' : 'Tooltip'} onReset={resetLegend} isDirty={isLegendDirty}>
        {caps.legend && (
          <>
            <Checkbox
              label="Show legend"
              checked={
                s.showLegend === 'auto'
                  ? (seriesNames.length > 1 || !caps.axis)
                  : s.showLegend === true
              }
              onChange={(_, data) => updateStyle({ showLegend: !!data.checked })}
            />
            {(s.showLegend === true || (s.showLegend === 'auto' && (seriesNames.length > 1 || !caps.axis))) && (
              <Field label="Legend Position">
                <RadioGroup layout="horizontal" value={s.legendPosition} onChange={(_, data) => updateStyle({ legendPosition: (data.value as LegendPosition) ?? "top" })}>
                  <Radio value="top" label="Top" />
                  <Radio value="bottom" label="Bottom" />
                  <Radio value="left" label="Left" />
                  <Radio value="right" label="Right" />
                </RadioGroup>
              </Field>
            )}
          </>
        )}
        {caps.sharedTooltip && (
          <Checkbox label="Shared tooltip" checked={s.sharedTooltip} onChange={(_, data) => updateStyle({ sharedTooltip: !!data.checked })} />
        )}
        {caps.crosshair && (
          <Checkbox label="Crosshair" checked={s.crosshair} onChange={(_, data) => updateStyle({ crosshair: !!data.checked })} />
        )}
      </VisualSection>
      )}

    </div>
  );
};

export default VisualEditor;